import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { User, onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";

interface UserProfile {
  user_id: string;
  roles: string[];
  store_id: string | null;
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("認証状態変更:", user ? "ログイン済み" : "ログアウト済み");
      setCurrentUser(user);

      if (user) {
        // プロフィール取得とFCM登録を並列実行せず、プロフィール取得を優先
        const fetchProfile = async () => {
          try {
            const token = await user.getIdToken();
            console.log("トークン取得成功、プロフィール取得を試行中...");

            // API ベース URL は環境変数優先（本番/検証は Worker のプレフィックス無しドメイン、
            // 未設定時はローカル Actix の /api/v1 付きにフォールバック）
            const apiBase =
              process.env.REACT_APP_API_BASE_URL ||
              "http://127.0.0.1:8080/api/v1";
            const response = await fetch(`${apiBase}/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const profile: UserProfile = await response.json();
              console.log("プロフィール取得成功:", profile);
              setUserProfile(profile);
            } else {
              console.error(
                "プロフィール取得失敗:",
                response.status,
                await response.text()
              );
              setUserProfile(null);
            }
          } catch (error) {
            console.error("プロフィール取得エラー:", error);
            setUserProfile(null);
          } finally {
            setLoading(false);
          }
        };

        // プロフィール取得を実行
        fetchProfile();

        // FCM登録は非同期で後から実行（ローディングに影響させない）
        setTimeout(() => {
          (async () => {
            try {
              const { requestNotificationPermission, registerFcmToken } =
                await import("./utils/fcm");
              const permissionResult = await requestNotificationPermission();

              if (permissionResult.granted && permissionResult.token) {
                const registered = await registerFcmToken(
                  permissionResult.token
                );
                if (registered) {
                  console.log("FCMトークンの登録に成功しました");
                } else {
                  console.warn("FCMトークンの登録に失敗しました");
                }
              }
            } catch (fcmError) {
              console.warn("FCM初期化エラー:", fcmError);
            }
          })();
        }, 1000); // 1秒後にFCM登録を開始
      } else {
        console.log("ユーザーがログアウトしました");
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  const value = useMemo(
    () => ({
      currentUser,
      userProfile,
      loading,
      logout,
    }),
    [currentUser, userProfile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
